import {
  flattenSections,
  iconDisplayName,
  isIconTabItem,
} from '../../utils/objectMenu';
import { type ObjectMenuSection } from './ObjectMenu';
import { type ObjectMenuItem } from './ObjectMenuController';
import { uniqBy } from 'lodash';
import { isFaIcon } from '../../utils/icons';
import { type IconData, type SpriteSvgItem } from '../../types';
import { shapeNameAliases } from '../../utils/jsonValidator';

const MAX_SEARCH_RESULTS = 100;

const spriteIconId = (item: SpriteSvgItem) => {
  const parts = item.path.split('-');
  return parts.slice(1).join('-');
};

const fontAwesomeId = (item: IconData) => {
  return item.iconName;
};

export const itemUniqueId = (item: ObjectMenuItem) => {
  return isIconTabItem(item) ? iconDisplayName(item) : item.stylusId();
};

const getMessageKey = (item: ObjectMenuItem) => {
  if (isIconTabItem(item)) {
    const id = (
      isFaIcon(item) ? fontAwesomeId(item) : spriteIconId(item)
    ).toLocaleLowerCase();
    return isFaIcon(item)
      ? id.replace(/_/g, ' ').replace(/-/g, ' ')
      : id
          // Massage the search terms with some domain knowledge
          .replace(
            /amazon_simple_storage_service/g,
            's3 simple storage service'
          )
          .replace(
            /aws_identity_access_management/g,
            'iam identity access management'
          )
          .replace(/aws_sts/g, 'sts security token service')
          .replace(/amazon_rds/g, 'rds relational database service')
          .replace(/amazon_vpc/g, 'vpc virtual private cloud')
          .replace(/elastic_ip_address/g, 'eip elastic ip address')
          .replace(/ec2/g, 'ec2 elastic compute cloud')
          .replace(/auto_scaling/g, 'auto scaling autoscaling')
          .replace(/aws/g, 'aws amazon')
          .replace(/identity_aware_proxy/g, 'iap identity aware proxy')
          .replace(/google_cloud/g, 'gcp google cloud')
          .replace(/azure/g, 'microsoft ms azure')
          .replace(/\.net/g, 'dotnet net')
          .replace(/_/g, ' ')
          .replace(/\//g, ' ')
          .replace(/\\/g, ' ')
          .replace(/-/g, ' ')
          .replace(/\./g, ' ');
  } else {
    // List of terms/aliases that this item can be searched by. Add to this on
    // a per item basis as needed.
    const terms = [item.description().toLowerCase()];

    // Add shape name aliases as search terms
    const stylusId = item.stylusId().toLowerCase();
    Object.entries(shapeNameAliases).forEach(([alias, shapeName]) => {
      if (stylusId.includes(shapeName)) {
        terms.push(alias);
      }
    });

    return terms.join(' ');
  }
};

const uniqItems = (sections: ObjectMenuSection<ObjectMenuItem>[]) => {
  const items = flattenSections(sections);
  return uniqBy(items, itemUniqueId);
};

const intersectSets = (sets: Set<ObjectMenuItem>[]): Set<ObjectMenuItem> => {
  if (sets.length === 0) return new Set();
  if (sets.length === 1) return sets[0];

  sets.sort((a, b) => a.size - b.size);
  let intersection = sets[0];

  for (let i = 1; i < sets.length; i++) {
    const nextSet = sets[i];
    const tempIntersection = new Set<ObjectMenuItem>();
    for (const item of intersection) {
      if (nextSet.has(item)) {
        tempIntersection.add(item);
      }
    }
    intersection = tempIntersection;

    if (intersection.size === 0) break;
  }

  return intersection;
};

interface SearchIndex {
  [word: string]: Set<ObjectMenuItem>;
}

// Given a list of sections, each containg a list of items, create a search
// index and provide the functions for searching that index.
class SearchManager {
  searchIndex: SearchIndex = {};
  searchKeys: string[];
  items: ObjectMenuItem[] = [];

  getItems(sections: ObjectMenuSection<ObjectMenuItem>[] = []) {
    if (this.items.length > 0) {
      return this.items;
    }

    // Only keep the sections that do not have the promoLink property as
    // those items are not available to this account.
    const availableSections = sections.filter(
      section =>
        !(typeof section.promoLink === 'function'
          ? section.promoLink()
          : section.promoLink)
    );

    this.items = uniqItems(availableSections);

    return this.items;
  }

  rebuildSearchIndex(sections: ObjectMenuSection<ObjectMenuItem>[]) {
    this.searchIndex = {};
    this.searchKeys = [];
    this.items = [];
    this.createSearchIndex(sections);
  }

  createSearchIndex = (sections: ObjectMenuSection<ObjectMenuItem>[]) => {
    const items = this.getItems(sections);

    items.forEach(item => {
      const massagedKey = getMessageKey(item);

      massagedKey.split(' ').forEach(word => {
        if (!word) return;
        if (!this.searchIndex[word]) {
          this.searchIndex[word] = new Set();
        }
        this.searchIndex[word].add(item);
      });
    });
    this.searchKeys = Object.keys(this.searchIndex).filter(
      key => !key.match(/[A-Z]/g)
    );
  };

  search(input: string) {
    if (!input) {
      return this.getItems().slice(0, MAX_SEARCH_RESULTS);
    }

    input = input.replace(/[^a-z0-9 ]/gi, '');

    // Split the input into search terms
    const searchTerms = input.toLowerCase().split(' ').filter(Boolean);

    // Find all words that match the search terms and union
    const sets = searchTerms.map(word => {
      const keys: string[] = [];
      // First check if the word is a key itself, this promotes
      // exact matches to the top of the results over string
      // search, which helps for things like aws, ms, gcp
      const set = this.searchIndex[word];
      if (set instanceof Set) {
        if (set.size >= 100) {
          return set;
        }
        if (set.size > 0) {
          keys.push(word);
        }
      }

      this.searchKeys
        .filter(key => key !== word && key.includes(word))
        .forEach(k => keys.push(k));

      // Union the Sets of each key we've matched for this word
      return new Set(
        keys.flatMap(key => {
          return [...this.searchIndex[key]];
        })
      );
    });

    // Intersect those unions to find the keys that match all search terms
    return [...intersectSets(sets)];
  }
}

export default SearchManager;
